/** Indian-market number formatting — the vocabulary every screen renders in. */

const nf = (min, max) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: min, maximumFractionDigits: max });

/** 4826000000 → "₹482.6 Cr" · 4820000 → "₹48.2 L" · 4820 → "₹4,820" */
export function inr(value, { decimals } = {}) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1e7) return `${sign}₹${nf(0, decimals ?? 2).format(abs / 1e7)} Cr`;
  if (abs >= 1e5) return `${sign}₹${nf(0, decimals ?? 2).format(abs / 1e5)} L`;
  return `${sign}₹${nf(0, decimals ?? 0).format(abs)}`;
}

/**
 * Scheme AUM as the fund master reports it — in ₹ crore, not rupees. Passing it
 * through `inr()` would render ₹88,560 for a fund holding ₹88,560 crore.
 */
export function inrCrore(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  if (Math.abs(v) >= 100000) return `₹${nf(0, 2).format(v / 100000)} L Cr`;
  return `₹${nf(0, 0).format(v)} Cr`;
}

/**
 * Dollars. The model providers bill in USD and the platform records their cost
 * in USD, so agent spend is the one figure in this app that is not rupees —
 * showing it as ₹ would be a 80× lie. Small amounts keep more decimals because
 * a single agent call costs cents.
 */
export function usd(value, { decimals } = {}) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  const dp = decimals ?? (abs >= 100 ? 0 : abs >= 1 ? 2 : 4);
  return `${sign}$${nf(0, dp).format(abs)}`;
}

/** Token counts — millions and thousands, because raw counts run to 9 digits. */
export function tokens(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  if (Math.abs(v) >= 1e9) return `${nf(0, 2).format(v / 1e9)}B`;
  if (Math.abs(v) >= 1e6) return `${nf(0, 2).format(v / 1e6)}M`;
  if (Math.abs(v) >= 1e3) return `${nf(0, 1).format(v / 1e3)}k`;
  return nf(0, 0).format(v);
}

/** Plain rupees with grouping — SIP amounts, ticket sizes. */
export function inrPlain(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `₹${nf(0, 0).format(Number(value))}`;
}

export function pct(value, decimals = 1) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${nf(decimals, decimals).format(Number(value))}%`;
}

/** Signed percentage — the leading ▲/▼ is left to the caller's chip/class. */
export function signedPct(value, decimals = 1) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${nf(decimals, decimals).format(Math.abs(v))}%`;
}

/**
 * Percentage POINTS — the gap between two rates, which is a different unit from
 * either of them. A hit rate of 61.7% against a base rate of 58.6% is worth
 * "+3.1 pp", and writing that as "+3.1%" invites it being read as a share of
 * the hit rate. Always signed, because a lift only means anything with its
 * direction attached.
 */
export function pp(value, decimals = 2) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Number(value);
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${nf(decimals, decimals).format(Math.abs(v))} pp`;
}

export function bps(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const v = Math.round(Number(value));
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v)} bps`;
}

export function num(value, decimals = 0) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return nf(decimals, decimals).format(Number(value));
}

/**
 * A stored risk figure, as the engine keeps it: rates are fractions, ratios are
 * plain numbers. Shared by the risk panels so a volatility reads the same
 * whether it is quoted on a scenario, a realised run or one holding's share.
 */
export function riskFigure(value, kind) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  /* Two places, like the ratios beside them. Most of what these panels quote is
     a CONTRIBUTION — one holding's slice of a fund's risk — and those sit below
     1%: at one place a column of them collapses to a handful of repeated values,
     and the figure disagreed with its own chart, whose axis reads two places. */
  return kind === 'pct' ? pct(Number(value) * 100, 2) : num(Number(value), 2);
}

/**
 * "Rajesh Sharma" → "RS" — avatar initials.
 *
 * Only letters count. Over half the accounts on this backend signed up by
 * phone, which stores the number as the name, and taking the first character
 * of each word put "+9" in the avatar; a label with no letters in it has no
 * initials, and says so.
 */
export function initials(name) {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}]/gu, ''))
    .filter(Boolean);
  if (!words.length) return '··';
  return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

/** up / down / flat class for a signed value, matching the design's .up/.down. */
export const tone = (value) => (Number(value) > 0 ? 'up' : Number(value) < 0 ? 'down' : '');

/**
 * A backend enum turned into a label — `model_portfolio` → "Model portfolio".
 * The stored values are lower-case with underscores, so putting one straight on
 * screen reads as a database column rather than a category.
 */
export function label(value, fallback = '—') {
  const raw = String(value ?? '').trim().replace(/[_-]+/g, ' ');
  if (!raw) return fallback;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

export function dateLong(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', {
    weekday: 'long', day: '2-digit', month: 'short', year: 'numeric',
  });
}

export function daysAgo(d) {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  const days = Math.round((Date.now() - dt.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

/**
 * Whole years since a date of birth, or null when there is no usable date.
 *
 * KYC stores the date as ISO (`1990-04-21`) on some records and day-first
 * (`21/04/1990`, `21-04-1990`) on others, so both are read. A birthday not yet
 * reached this year takes a year off.
 */
export function ageFromDob(dob) {
  if (!dob) return null;
  const s = String(dob).trim();
  const dayFirst = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const dt = dayFirst ? new Date(Number(dayFirst[3]), Number(dayFirst[2]) - 1, Number(dayFirst[1])) : new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dt.getFullYear();
  if (now.getMonth() < dt.getMonth() || (now.getMonth() === dt.getMonth() && now.getDate() < dt.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/** Greeting used by the dashboard headline. */
export function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * A backend error as a person should read it. GraphQL failures arrive with the
 * HTTP status in the text, nested when one layer wraps another —
 * "500: 401: Invalid email or password".
 */
export const plainError = (err, fallback) =>
  String(err?.message || fallback).replace(/^(\d{3}:\s*)+/, '');

/**
 * A phone number as the backend stores it, or null: digits with a leading '+'
 * kept, nothing else. The backend normalises again on its side — this is only
 * so what a form sends is what a person typed, minus the spacing.
 */
export const plainPhone = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) return null;
  return raw.startsWith('+') ? `+${digits}` : digits;
};

/**
 * Whether a phone number is one someone could actually be called on: ten
 * digits at least (an Indian mobile, with or without its country code).
 * Used where a contact number is required rather than optional.
 */
export const isReachablePhone = (value) => (String(value ?? '').replace(/[^\d]/g, '').length >= 10);

/** Whether a string is nothing but a phone number (a name that is really one). */
export const looksLikePhone = (value) => /^\+?[\d\s()-]{8,}$/.test(String(value ?? '').trim());

/**
 * A phone number written so it reads as a contact: "+919502042793" →
 * "+91 95020 42793". Anything too short to be one is returned untouched.
 */
export function phoneLabel(value) {
  const raw = String(value ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10) return raw || null;
  const local = digits.slice(-10);
  const code = digits.slice(0, -10);
  return `${code ? `+${code} ` : ''}${local.slice(0, 5)} ${local.slice(5)}`;
}
